import React, { useMemo, useState } from 'react'

import { useDebounce } from 'use-debounce'
import {
  Box,
  Button,
  FormControl,
  Input,
  InputGroup,
  Spinner,
  Text,
} from '@chakra-ui/react'

import { ATSJob } from '@pkg/types/api'

import {
  getATSJobDepartment,
  getATSJobEmploymentType,
  getATSJobLocation,
  getATSJobTitle,
  useListATSJobs,
} from '@/react-query/ats/jobs'
import { useInterview } from '@/react-query/interview/interview'
import { useCreateRole, useRole, useUpdateRole } from '@/react-query/role'

export function InputATSJob() {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedJob, setSelectedJob] = useState<ATSJob | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [showResults, setShowResults] = useState(false)

  // Debounce the search query to prevent excessive API calls
  const [debouncedQuery] = useDebounce(searchQuery, 300)

  const createRole = useCreateRole()
  const updateRole = useUpdateRole()
  const role = useRole()
  const { data: interview } = useInterview()

  const { data: searchResults, isLoading: isSearching } = useListATSJobs(
    debouncedQuery,
    {
      enabled: debouncedQuery.trim().length > 1,
    },
  )

  // Determine if we should show results based on current input and debounced query
  const shouldShowResults = useMemo(() => {
    return showResults && searchQuery.trim().length > 1
  }, [showResults, searchQuery])

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setSearchQuery(value)
    setShowResults(value.trim().length > 1)
    setSelectedJob(null)
  }

  const handleJobSelect = (atsJob: ATSJob) => {
    setSelectedJob(atsJob)
    setSearchQuery(getATSJobTitle(atsJob))
    setShowResults(false)
  }

  const handleCreateFromATSJob = async () => {
    if (!selectedJob) return

    setIsCreating(true)
    try {
      // Build a job description from available fields if description is empty
      let jobDescription = selectedJob.description || ''

      if (!jobDescription) {
        const descriptionParts = []

        if (selectedJob.department && selectedJob.department.length > 0) {
          descriptionParts.push(
            `Department: ${selectedJob.department.join(', ')}`,
          )
        }

        if (selectedJob.location && selectedJob.location.length > 0) {
          descriptionParts.push(`Location: ${getATSJobLocation(selectedJob)}`)
        }

        if (selectedJob.employmentType) {
          descriptionParts.push(
            `Employment Type: ${getATSJobEmploymentType(selectedJob)}`,
          )
        }

        if (selectedJob.status) {
          descriptionParts.push(`Status: ${selectedJob.status}`)
        }

        jobDescription = descriptionParts.join('\n')
      }

      const roleData = {
        jobTitle: getATSJobTitle(selectedJob),
        jobDescription,
      }

      if (role.data?.id) {
        await updateRole.mutateAsync({
          role: {
            id: role.data.id,
            ...roleData,
          },
        })
      } else {
        await createRole.mutateAsync({
          role: roleData,
        })
      }

      // Clear the search after successful creation
      setSearchQuery('')
      setSelectedJob(null)
    } catch (error) {
      console.error('Error creating role from ATS job:', error)
    } finally {
      setIsCreating(false)
    }
  }

  const handleBlur = () => {
    // Delay hiding results to allow for click events
    setTimeout(() => setShowResults(false), 200)
  }

  const interviewType = interview?.type
  const roleWord = interviewType === 'INTAKE' ? 'role' : 'job'

  return (
    <Box position="relative" mt={5} overflow="visible">
      <FormControl>
        <InputGroup
          display="flex"
          flexDirection="row"
          background="white"
          rounded="full"
          position="relative"
        >
          <Input
            placeholder={`Search ATS ${roleWord}s...`}
            variant="brand-solid-white"
            rounded="full"
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={() => {
              if (searchQuery.trim().length > 1) {
                setShowResults(true)
              }
            }}
            onBlur={handleBlur}
          />
          {selectedJob && (
            <Button
              rounded="1.25rem"
              isLoading={isCreating}
              onClick={handleCreateFromATSJob}
              display="flex"
              fontWeight="bold"
              variant="brand-solid-white"
              px={5}
              flexShrink={0}
            >
              {role.data?.id ? 'Update' : 'Create'}
            </Button>
          )}
        </InputGroup>
      </FormControl>

      {/* Search Results Dropdown - styled like MenuList */}
      {shouldShowResults && (
        <Box
          position="absolute"
          top="100%"
          left={0}
          right={0}
          zIndex={1000}
          mt={2}
          bg="white"
          rounded="1.25rem"
          boxShadow="0px 4px 30px 0px rgba(0, 0, 0, 0.25)"
          py={0}
          maxHeight="20rem"
          overflowY="auto"
          overflowX="hidden"
          minWidth="300px"
          willChange="transform, opacity"
        >
          {isSearching || debouncedQuery !== searchQuery ? (
            <Box px={4} py={2.5} _first={{ pt: 3.5 }} _last={{ pb: 3.5 }}>
              <Box display="flex" alignItems="center">
                <Spinner size="sm" mr={2} />
                <Text fontWeight="bold">Searching...</Text>
              </Box>
            </Box>
          ) : searchResults && searchResults.length > 0 ? (
            searchResults.map((atsJob) => (
              <Box
                key={atsJob.id || atsJob.atsId}
                px={4}
                py={2.5}
                cursor="pointer"
                bg="white"
                _hover={{ bg: '#f6f6f6' }}
                _first={{ pt: 3.5 }}
                _last={{ pb: 3.5 }}
                onClick={() => handleJobSelect(atsJob)}
                transition="all 0.2s"
                role="menuitem"
                fontWeight="bold"
              >
                <Box width="100%">
                  <Text fontWeight="bold">{getATSJobTitle(atsJob)}</Text>
                  <Text
                    fontSize="xs"
                    color="gray.600"
                    mt={1}
                    fontWeight="normal"
                  >
                    {[
                      getATSJobLocation(atsJob),
                      getATSJobEmploymentType(atsJob),
                      getATSJobDepartment(atsJob),
                    ]
                      .filter(Boolean)
                      .join(' • ')}
                  </Text>
                </Box>
              </Box>
            ))
          ) : debouncedQuery.trim().length > 1 ? (
            <Box
              px={4}
              py={2.5}
              _first={{ pt: 3.5 }}
              _last={{ pb: 3.5 }}
              color="gray.500"
              fontWeight="bold"
            >
              No {roleWord}s found
            </Box>
          ) : null}
        </Box>
      )}
    </Box>
  )
}
